import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

// The web origin our Universal Links / App Links live under. A deep link that
// opens the app carries the full https://petzy.ro/... URL; we translate the
// path+search+hash into an in-app router navigation so the link lands on the
// right screen (e.g. a booking-notification email → /salon/appointments,
// or the Stripe return → /salon/billing?success=1 with the query preserved).
const APP_ORIGINS = ['petzy.ro', 'www.petzy.ro'];

// Native deep-link bridge: subscribe to @capacitor/app 'appUrlOpen' and route
// the incoming URL through React Router. No-op on web (event never fires) and
// dormant until the native projects declare associated domains / app links —
// so mounting it in the client app is harmless (nothing routes to it yet).
export default function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor?.isNativePlatform?.()) return undefined;

    let removeListener;
    let cancelled = false;

    import('@capacitor/app')
      .then(({ App }) => {
        const handlePromise = App.addListener('appUrlOpen', ({ url }) => {
          if (!url) return;
          let target = null;
          try {
            const parsed = new URL(url);
            // Only follow links on our own domain — never navigate off a URL
            // some other app handed us. Custom-scheme URLs (no http host)
            // are ignored here; OAuth callbacks use their own plugin flow.
            if (APP_ORIGINS.includes(parsed.host)) {
              target = parsed.pathname + parsed.search + parsed.hash;
            }
          } catch {
            /* malformed URL — ignore */
          }
          if (target) navigate(target);
        });
        handlePromise.then((handle) => {
          if (cancelled) handle.remove();
          else removeListener = () => handle.remove();
        });
      })
      .catch(() => {
        /* plugin unavailable — nothing to wire up */
      });

    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
  }, [navigate]);
}
